import includes from 'lodash/includes'
import * as React from 'react'
import {
  Outlet,
  redirect,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRevalidator,
  type LoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs
} from 'react-router'
import {mthen} from '@freckle/maybe'
import StorageHelper from '@frontrow/console/src/entities/helpers/storage-helper'

import {fetchConsoleAgent, type ConsoleAgentT} from '@frontrow/console/src/helpers/auth-helpers'
import AgentContext from '@frontrow/console/src/context/agent'
import Dashboard from '@frontrow/console/src/components/common/dashboard'
import {PendoWrapper} from '@frontrow/console/src/components/pendo-wrapper'

const CONSOLE_LOGIN_REDIRECT_KEY = 'consoleLoginRedirect'

// Runs before any signed-in page renders, so no page renders without a session.
export async function signedInLoader({request}: LoaderFunctionArgs): Promise<ConsoleAgentT> {
  const agent = await fetchConsoleAgent().catch(() => {
    setRedirectCookie(new URL(request.url).pathname)
    throw redirect('/login')
  })

  const loginRedirect = getRedirect()
  if (
    loginRedirect !== null &&
    loginRedirect !== undefined &&
    isAppPath(loginRedirect) &&
    loginRedirect !== new URL(request.url).pathname
  ) {
    throw redirect(loginRedirect)
  }

  return agent
}

// Re-check the session on every page change, including a param change. Data tables
// write their filters to the query string, so a change to it alone is skipped. The
// same URL is a revalidation (from the tab becoming visible) and always runs.
export function shouldRecheckSession({currentUrl, nextUrl}: ShouldRevalidateFunctionArgs): boolean {
  return currentUrl.pathname !== nextUrl.pathname || currentUrl.search === nextUrl.search
}

export function SignedInLayout(): React.ReactElement {
  const agent = useLoaderData<typeof signedInLoader>()
  const matches = useMatches()
  useRecheckSessionWhenTabShown()

  return (
    <AgentContext.Provider value={agent}>
      <PendoWrapper agent={agent}>
        <Dashboard>
          {/* Keyed so each route mounts a fresh page, as each v5 `<Route>` did.
              Routes that share a page component would otherwise keep its state. */}
          <Outlet key={matches[matches.length - 1]?.id} />
        </Dashboard>
      </PendoWrapper>
      <ScrollRestoration />
    </AgentContext.Provider>
  )
}

function useRecheckSessionWhenTabShown() {
  const {revalidate} = useRevalidator()

  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchConsoleAgent().catch(() => {
          alert('You have been disconnected')
          // The loader fails the same check and redirects to /login.
          void revalidate()
        })
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [revalidate])
}

function setRedirectCookie(pathname: string) {
  // The default route should be here to avoid redirecting to the same route on login
  const avoidRedirect = ['/', '/login', '/teachers']
  if (!includes(avoidRedirect, pathname)) {
    StorageHelper.setSecureCookie(
      CONSOLE_LOGIN_REDIRECT_KEY,
      encodeURIComponent(pathname),
      null,
      '/'
    )
  }
}

// The cookie is shared with sibling subdomains, and a loader redirect follows an
// absolute URL off the site, so only a path within the app is followed.
function isAppPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
}

function getRedirect(): string | undefined | null {
  const mRedirect = StorageHelper.getCookie(CONSOLE_LOGIN_REDIRECT_KEY)

  // With the cookie's own path: the loader runs before the URL changes, and
  // without it the browser would expire a cookie at the current page's directory.
  StorageHelper.setSecureCookie(CONSOLE_LOGIN_REDIRECT_KEY, 'v', -1, '/')

  return mthen(mRedirect, redirect => decodeURIComponent(redirect))
}
